'use client';

import { useEffect } from 'react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('[CrewSync] Unhandled error:', error);
    // Ship the error to a server endpoint so we can grep Vercel Runtime
    // Logs for the stack trace. Diagnostic for the iPhone-Safari-only
    // ChatWidgetDemo crash — remove with #137 follow-up.
    try {
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source: 'error-boundary',
          message: error?.message || '',
          stack: error?.stack || '',
          digest: error?.digest || '',
          url: typeof window !== 'undefined' ? window.location.href : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : '',
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // swallow — we're already in an error state
    }
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-5">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[var(--text-primary)] mb-2">
          Something went wrong
        </h2>
        <p className="text-sm text-[var(--text-secondary)] mb-6 leading-relaxed">
          An unexpected error occurred. You can try again or go back to the dashboard.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={reset}
            className="btn-primary"
          >
            Try again
          </button>
          <a
            href="/"
            className="btn-secondary"
          >
            Go to Dashboard
          </a>
        </div>
        {/* Diagnostic surface — temporarily shown in prod to debug the
            iPhone-Safari-only ChatWidgetDemo crash. Remove after fix. */}
        {(error?.message || error?.digest) && (
          <pre className="mt-6 text-left text-[11px] bg-red-500/5 border border-red-500/10 rounded-xl p-3 overflow-auto max-h-48 text-red-400 whitespace-pre-wrap break-words">
            {error?.digest ? `digest: ${error.digest}\n` : ''}{error?.message || '(no message)'}
            {error?.stack ? `\n\n${error.stack.split('\n').slice(0, 8).join('\n')}` : ''}
          </pre>
        )}
      </div>
    </div>
  );
}
